import React, { useEffect, useState } from "react";

import type { ClientId } from "../../domain/types";
import type { ZohoSeatDTO } from "../../data/zohoConnectApi";
import { claimZohoSeat, setZohoDc, setZohoOrgId, startZohoConnect } from "../../data/zohoConnectApi";
import { persistAnswer, useCoAnswer, useCoClient } from "../../data/store";
import { ZohoInviteFields } from "../../components/ZohoInviteFields";
import { ExplainerConfirm } from "../../components/ExplainerConfirm";
import { SubStepShell } from "../../components/SubStepShell";
import { useSubStepNav } from "../useSubStepNav";

/**
 * "The client has added our user." Read by Confirm connection too: that step
 * only starts hunting for an invitation once someone has said one was sent,
 * so arriving there through the sidebar cannot burn the waiting deadline down
 * on a client nobody has invited us to yet.
 */
export const ADMIN_USER_EXPLAINER_KEY = "adminUserExplainer";
const EXPLAINER_KEY = ADMIN_USER_EXPLAINER_KEY;

const REASSURANCE =
  "Zournal never deletes entries or files anything without your approval. Every action is logged. You can remove access anytime.";

/**
 * Which organisation are we joining?
 *
 * Asked before the address is handed over, and deliberately blocking. Once we
 * hold the id, confirming the connection is a lookup — is this organisation in
 * the list Zoho gives us — instead of working out which of the organisations we
 * can suddenly see must be the client's. That inference is answerable wrongly,
 * and a wrong answer binds a client to someone else's books.
 *
 * The number is in the address bar of the Zoho Books tab the client already has
 * open to send the invitation, so the URL is accepted as-is; the server pulls
 * the id out of it.
 */
function OrgIdGate({
  value,
  editable,
  onSave,
}: {
  value: string | null;
  /**
   * Whether the saved id can still be changed. Mirrors what the server allows —
   * `setOrgId` refuses once an invitation has been found — so the link is absent
   * rather than present and failing.
   */
  editable: boolean;
  onSave: (raw: string) => Promise<void>;
}) {
  const [raw, setRaw] = useState("");
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | undefined>();

  // The id is typed by hand off a URL, so the common reason to reopen this is a
  // digit that came across wrong. Prefilling makes that a one-character fix
  // instead of a re-paste.
  function startEditing() {
    setRaw(value ?? "");
    setError(undefined);
    setEditing(true);
  }

  if (value && !editing) {
    return (
      <div className="flex items-center gap-2 rounded-lg border px-5 py-3 text-[13px]">
        <span className="text-muted-foreground">Client's Zoho organisation</span>
        <span className="font-mono text-foreground">{value}</span>
        {editable && (
          <button
            type="button"
            onClick={startEditing}
            className="ml-auto shrink-0 text-[13px] text-muted-foreground underline-offset-2 hover:text-foreground hover:underline"
          >
            Edit
          </button>
        )}
      </div>
    );
  }

  async function handleSave() {
    if (!raw.trim() || saving) return;
    setSaving(true);
    setError(undefined);
    try {
      await onSave(raw.trim());
      setEditing(false);
    } catch (err: unknown) {
      const data = (err as { data?: { errors?: Record<string, string> } })?.data;
      // The save can be refused for reasons keyed on other fields too — most
      // commonly "no seat reserved yet" (public_id) — and a bare "Validation
      // failed" here sent an operator hunting a URL-format problem that
      // wasn't one.
      setError(
        data?.errors?.org_id
          ?? data?.errors?.public_id
          ?? (data?.errors ? Object.values(data.errors)[0] : undefined)
          ?? (err instanceof Error ? err.message : "Could not save that.")
      );
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="flex flex-col gap-2 rounded-lg border px-5 py-4">
      <span className="text-[15px] font-medium">Enter the client's Zoho Organisation ID</span>
      {/* The placeholder already shows the shape of a Books URL, so a help note
          repeating it was the same example twice. Anyone who needs to know where
          the number lives finds it by looking at the tab they already have open. */}
      <span className="text-[13px] text-muted-foreground">
        You can copy and paste either the Zoho Organisation ID or the Zoho Books URL here.
      </span>

      <div className="mt-1 flex items-center gap-2">
        <input
          value={raw}
          onChange={(e) => setRaw(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") void handleSave();
            if (e.key === "Escape" && value) setEditing(false);
          }}
          autoFocus={editing}
          placeholder="https://books.zoho.in/app/60000000000#/home or https://books.zoho.com/app/…"
          className="min-w-0 flex-1 rounded-md border bg-transparent px-3 py-2 font-mono text-[13px] outline-none focus:border-primary"
        />
        <button
          type="button"
          onClick={() => void handleSave()}
          disabled={!raw.trim() || saving}
          className="shrink-0 rounded-md bg-primary px-3 py-2 text-xs font-medium text-primary-foreground transition-opacity disabled:opacity-40"
        >
          {saving ? "Saving…" : "Save"}
        </button>
        {/* Only while editing: there is nothing to go back to on a first answer. */}
        {editing && value && (
          <button
            type="button"
            onClick={() => setEditing(false)}
            disabled={saving}
            className="shrink-0 px-1 text-[13px] text-muted-foreground hover:text-foreground disabled:opacity-40"
          >
            Cancel
          </button>
        )}
      </div>
      {/* Changing it after the address has gone out is legitimate — it is how a
          wrong organisation gets corrected — but it is worth saying that the
          invitation has to follow, because the server stops trusting any that
          arrived before the correction. */}
      {editing && value && (
        <span className="text-[13px] text-muted-foreground">
          Changing this means only invitations that arrive from now on count. Ask the client
          to send a fresh one from the corrected organisation.
        </span>
      )}
      {error && <span className="text-[13px] text-amber-600">{error}</span>}
    </div>
  );
}

/** The two datacentres clients are on. Others exist; none are offered. */
const DATACENTRES = [
  { value: "in", label: "India (.in)" },
  { value: "com", label: "US (.com)" },
];

/**
 * Which Zoho datacentre the client's books are on.
 *
 * Zoho's datacentres are isolated: a bot account on `.in` can never be invited
 * into a `.com` organisation. The DC is normally learnt from the Books URL
 * pasted above, so this row mostly just says what was learnt; **Change** is for
 * the case where the CA typed the bare number and the seat is still on the
 * default `.in` bot. Changing it swaps the address on screen, which is why it
 * locks the moment an invitation has been found.
 */
function DatacentreRow({
  dc,
  label,
  editable,
  onChange,
}: {
  dc: string | null;
  label: string;
  editable: boolean;
  onChange: (dc: string) => Promise<void>;
}) {
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | undefined>();

  async function handlePick(next: string) {
    if (next === (dc ?? "in")) {
      setEditing(false);
      return;
    }
    setSaving(true);
    setError(undefined);
    try {
      await onChange(next);
      setEditing(false);
    } catch (err: unknown) {
      const data = (err as { data?: { errors?: Record<string, string> } })?.data;
      setError(
        data?.errors?.dc
          ?? (data?.errors ? Object.values(data.errors)[0] : undefined)
          ?? (err instanceof Error ? err.message : "Could not change the datacentre.")
      );
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="flex flex-col gap-1 rounded-lg border px-5 py-3 text-[13px]">
      <div className="flex items-center gap-2">
        <span className="text-muted-foreground">Datacentre</span>
        {editing ? (
          <select
            autoFocus
            disabled={saving}
            value={dc ?? "in"}
            onChange={(e) => void handlePick(e.target.value)}
            className="rounded-md border bg-transparent px-2 py-1 text-[13px] outline-none focus:border-primary"
          >
            {DATACENTRES.map((d) => (
              <option key={d.value} value={d.value}>
                {d.label}
              </option>
            ))}
          </select>
        ) : (
          <span className="font-medium text-foreground">{label}</span>
        )}
        {saving && <span className="text-muted-foreground">Re-reserving a user…</span>}
        {editable && !saving && (
          <button
            type="button"
            onClick={() => setEditing((v) => !v)}
            className="ml-auto shrink-0 text-[13px] text-muted-foreground underline-offset-2 hover:text-foreground hover:underline"
          >
            {editing ? "Cancel" : "Change"}
          </button>
        )}
      </div>
      {error && <span className="text-[12px] text-destructive">{error}</span>}
    </div>
  );
}

/**
 * The client adds our bot Zoho account as an admin user on their own Zoho Books.
 *
 * The address is not a constant: it is a seat reserved out of the bot-account
 * pool by the backend, claimed here on load rather than on a button press. That
 * ordering matters — once the client has copied an address into their Zoho, a
 * reload must not hand them a different one, and nothing on screen would reveal
 * the swap.
 *
 * This step ends at "we've added the user". Accepting the invitation, verifying
 * the API access and mapping the organisation are watched on Confirm connection,
 * the step after: they take minutes, and a screen whose job is "copy these three
 * fields into Zoho" should not also be the screen you sit and stare at. The
 * confirm click still starts the hunt, so the wait runs down while the CA reads.
 */
export function AddZournalStep({ clientId }: { clientId: ClientId }) {
  const client = useCoClient(clientId);
  const { goNext } = useSubStepNav(clientId, "add-zournal");
  const confirmed = useCoAnswer<boolean>(clientId, EXPLAINER_KEY);

  const [seat, setSeat] = useState<ZohoSeatDTO | undefined>();
  const [seatError, setSeatError] = useState<string | undefined>();
  // Set by the gate so it can collapse without waiting for a seat re-fetch.
  const [savedOrgId, setSavedOrgId] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    claimZohoSeat(clientId)
      .then((claimed) => {
        if (!cancelled) setSeat(claimed);
      })
      .catch((err: unknown) => {
        if (!cancelled) {
          setSeatError(err instanceof Error ? err.message : "Could not reserve a Zournal user.");
        }
      });
    return () => {
      cancelled = true;
    };
  }, [clientId]);

  const orgId = savedOrgId ?? seat?.expected_org_id ?? null;

  // The same window the server enforces in setOrgId. Unknown while the seat is
  // still loading, and open by default there: no id is on screen to edit yet.
  const orgIdEditable =
    seat === undefined || seat.status === "seat_claimed" || seat.status === "awaiting_invite";

  async function handleSaveOrgId(raw: string) {
    const next = await setZohoOrgId(clientId, raw);
    setSavedOrgId(next.expected_org_id);
    // A pasted URL may have moved the seat to another datacentre's bot, and
    // with it the address — re-read rather than show a stale one.
    if (next.dc !== (seat?.dc ?? null)) {
      setSeat(await claimZohoSeat(clientId));
    }
  }

  async function handleChangeDc(dc: string) {
    await setZohoDc(clientId, dc);
    // The address on screen belongs to whichever bot now holds the seat.
    setSeat(await claimZohoSeat(clientId));
  }

  function handleConfirm() {
    void persistAnswer(clientId, EXPLAINER_KEY, true);
    // Start the hunt here rather than on the next screen, so the mailbox is
    // already being watched while the CA walks over. Failures are not surfaced:
    // Confirm connection calls start again on mount, and start() only moves a
    // seat that is still sitting at seat_claimed.
    void startZohoConnect(clientId).catch(() => undefined);
  }

  if (!client) return null;

  const seatEmail = seat?.claimed_email ?? undefined;

  return (
    <SubStepShell
      title="Connect Zournal to Zoho Books"
      description={`Add Zournal as an admin user on ${client.name || "this client"}'s Zoho Books.`}
      thenWhat="Once you have invited the Zournal user as an Admin, click Continue."
      onContinue={() => void goNext()}
      continueDisabled={!seatEmail || !orgId || !confirmed}
    >
      {/* No plan card here. It is reference for a decision already made and
          shown on Zoho account, the step immediately before — repeating it puts
          a four-column table above the one thing this screen asks for. */}
      <OrgIdGate value={orgId} editable={orgIdEditable} onSave={handleSaveOrgId} />

      {/* Shown once a seat exists, so the CA sees which Zoho the address below
          can actually join. Same lock window as the org id. */}
      {seat && (
        <DatacentreRow
          dc={seat.dc}
          label={seat.dc_label}
          editable={orgIdEditable}
          onChange={handleChangeDc}
        />
      )}

      {/* The address stays hidden until we know the organisation. Handing it
          over first would let an invitation arrive for an organisation we have
          no way to identify, which is the one situation the agent cannot
          resolve without guessing. */}
      {orgId && (
        <ExplainerConfirm
          title="Add Zournal as an admin user"
          explainer={
            seatEmail
              ? "The next step is to add Zournal as an Admin user in the client's Zoho Books account. Go to Settings → Users & Roles and invite a new user. Copy and paste the details provided below."
              : "Reserving the Zournal user for this client…"
          }
          videoCaption="How to add a Zournal user to Zoho"
          // Official Zoho Books video: "How to Set Up User Roles and Invite Users".
          videoId="FL4INMHvg-o"
          reassurance={REASSURANCE}
          confirmLabel="We've added the user"
          confirmed={Boolean(confirmed)}
          onConfirm={handleConfirm}
        >
          {seatEmail ? (
            <ZohoInviteFields email={seatEmail} />
          ) : (
            // No address, nothing safe to copy — an operator has to add capacity
            // before this client can go any further.
            <div className="rounded-md border border-dashed px-3 py-2 text-[13px] text-muted-foreground">
              {seatError ?? "Reserving a Zournal user…"}
            </div>
          )}
        </ExplainerConfirm>
      )}
    </SubStepShell>
  );
}
